import { addDays, addHours, endOfDay, format, startOfDay } from "date-fns";
import {
  ADVERTISERS_DEMO,
  FACEBOOK,
  FACEBOOK_CONTENT_TYPE_POST,
  INSTAGRAM,
  INSTAGRAM_CONTENT_TYPE_IMAGE,
  INSTAGRAM_CONTENT_TYPE_STORIES,
  STATES,
} from "./constants";

const CONTENT_SCHEDULED_QUEUE = "scheduledContentsQueue";
const HOUR_MS = 60 * 60 * 1000;
const DEMO_SLOT_MIN_HOUR = 9;
const DEMO_SLOT_MAX_HOUR = 20;

export const DEMO_ORGANIC_CONTENT_TEMPLATES = [
  {
    scheduleID: "demo-organic-ig-story",
    platforms: [INSTAGRAM],
    platformsData: {
      [INSTAGRAM]: {
        mediaType: INSTAGRAM_CONTENT_TYPE_STORIES,
        integrationID: "",
        pageID: "",
        instagramID: "",
      },
    },
    urls: [
      "https://clapp-prod.s3.us-east-1.amazonaws.com/ai-generated-images/-OUG2ATB4solSpsV5MC-/722b230e-a4ee-475c-a058-ec8bd48422b8.png",
    ],
    description:
      "Agiliza tu gestión de paid media. Ahorra tiempo, escala más rápido. 🚀\n\nAutomatiza tu operación en Meta, Google y TikTok desde un solo lugar. Campañas, clics y datos — todo bajo control.\n\n¿Listo para optimizar? Conoce más 👉",
    language: "es",
  },
  {
    scheduleID: "demo-organic-fb-post",
    platforms: [FACEBOOK],
    platformsData: {
      [FACEBOOK]: {
        mediaType: FACEBOOK_CONTENT_TYPE_POST,
        integrationID: "",
        pageID: "",
      },
    },
    urls: [
      "https://clapp-prod.s3.us-east-1.amazonaws.com/ai-generated-images/-OUG2ATB4solSpsV5MC-/11367ead-2138-4d4e-8328-fb99287a29f1.png",
    ],
    description:
      "Cuando los anuncios se vuelven más inteligentes, las agencias crecen más rápido. 📈\n\nOptimiza tus campañas en Meta, Google y TikTok desde un solo dashboard. Métricas en tiempo real, ROAS y performance al alcance de tu mano.\n\nAprende cómo funciona adle 👇",
    language: "es",
  },
  {
    scheduleID: "demo-organic-ig-fb-post",
    platforms: [INSTAGRAM, FACEBOOK],
    platformsData: {
      [INSTAGRAM]: {
        mediaType: INSTAGRAM_CONTENT_TYPE_IMAGE,
        integrationID: "",
        pageID: "",
        instagramID: "",
      },
      [FACEBOOK]: {
        mediaType: FACEBOOK_CONTENT_TYPE_POST,
        integrationID: "",
        pageID: "",
      },
    },
    urls: [
      "https://clapp-prod.s3.us-east-1.amazonaws.com/ai-generated-images/-OUG2ATB4solSpsV5MC-/4b7fe100-5bbd-470a-bcca-12b25f5f3044.png",
    ],
    description:
      "Agiliza tu gestión de paid media. Ahorra tiempo, escala más rápido. 🚀\n\nAutomatiza tu operación en Meta, Google y TikTok desde un solo lugar. Campañas, clics y datos — todo bajo control.\n\n¿Listo para optimizar? Conoce más 👉",
    language: "es",
  },
];

const hashSeed = (value) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
};

const createSeededRandom = (seed) => {
  let state = seed;

  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
};

const shuffleWithSeed = (items, random) => {
  const shuffledItems = [...items];

  for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffledItems[index], shuffledItems[swapIndex]] = [
      shuffledItems[swapIndex],
      shuffledItems[index],
    ];
  }

  return shuffledItems;
};

const buildCandidateSlots = (now) => {
  const windowStartMs = now.getTime() + HOUR_MS;
  const windowEndMs = endOfDay(addDays(startOfDay(now), 6)).getTime();
  const slots = [];

  let cursor = new Date(windowStartMs);
  cursor.setMinutes(0, 0, 0);

  if (cursor.getTime() < windowStartMs) {
    cursor = addHours(cursor, 1);
  }

  while (cursor.getTime() <= windowEndMs) {
    const hour = cursor.getHours();

    if (hour >= DEMO_SLOT_MIN_HOUR && hour <= DEMO_SLOT_MAX_HOUR) {
      slots.push(cursor.getTime());
    }

    cursor = addHours(cursor, 1);
  }

  return slots;
};

const buildScheduledStates = (platforms) =>
  platforms.reduce((states, platform) => {
    states[platform] = STATES.SCHEDULED;
    return states;
  }, {});

export const shouldInjectDemoOrganicContents = (advertiserID) =>
  Boolean(advertiserID) && ADVERTISERS_DEMO.includes(advertiserID);

export const buildDemoScheduledContents = ({
  advertiserID,
  userID,
  now = new Date(),
}) => {
  const dayKey = format(startOfDay(now), "yyyy-MM-dd");
  const random = createSeededRandom(hashSeed(`${dayKey}-${advertiserID}`));
  const candidateSlots = buildCandidateSlots(now);
  const shuffledSlots = shuffleWithSeed(candidateSlots, random);
  const fallbackSlot =
    shuffledSlots[shuffledSlots.length - 1] ?? now.getTime() + HOUR_MS;

  return DEMO_ORGANIC_CONTENT_TEMPLATES.reduce(
    (demoContents, template, index) => {
      const scheduledDate = shuffledSlots[index] ?? fallbackSlot;

      demoContents[template.scheduleID] = {
        userID,
        advertiserID: String(advertiserID),
        creationTime: now.getTime(),
        queue: CONTENT_SCHEDULED_QUEUE,
        scheduleID: template.scheduleID,
        scheduledDate,
        urls: template.urls,
        language: template.language || "es",
        description: template.description || "",
        cloudTaskID: null,
        platforms: template.platforms,
        states: buildScheduledStates(template.platforms),
        platformsData: template.platformsData,
        isDemoContent: true,
      };

      return demoContents;
    },
    {}
  );
};

export const mergeDemoScheduledContents = (
  realContents,
  { advertiserID, userID, now = new Date() } = {}
) => {
  if (!shouldInjectDemoOrganicContents(advertiserID)) {
    return realContents || {};
  }

  const demoContents = buildDemoScheduledContents({
    advertiserID,
    userID,
    now,
  });

  return {
    ...(realContents || {}),
    ...demoContents,
  };
};
